import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { countryApi } from 'api/countryApi';

const CountryContext = createContext();

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within CountryProvider');
  }
  return context;
};

export const CountryProvider = ({ children }) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Cache duration: 10 minutes
  const CACHE_DURATION = 10 * 60 * 1000;

  const fetchCountries = useCallback(async (forceRefresh = false) => {
    // Check cache if not forcing refresh
    if (!forceRefresh && countries.length > 0 && lastFetchTime) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return countries;
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      const options = {
        isActive: true,
        sortBy: 'countryName',
        sortOrder: 'ASC'
      };

      const response = await countryApi.getActiveCountries(options);
      
      if (response.countries) {
        const sortedCountries = [...response.countries].sort((a, b) => 
          a.countryName.localeCompare(b.countryName)
        );
        
        setCountries(sortedCountries);
        setLastFetchTime(Date.now());
        return sortedCountries;
      }
      
      return [];
    } catch (err) {
      console.error('Error fetching countries:', err);
      setError(err.message || 'Failed to load countries');
      return [];
    } finally {
      setLoading(false);
    }
  }, [countries, lastFetchTime]);

  const getCountryById = useCallback((id) => {
    return countries.find(c => c.id === id) || null;
  }, [countries]);

  const getCountryByCode = useCallback((code) => {
    return countries.find(c => c.countryCode === code) || null;
  }, [countries]);

  const getDefaultCountry = useCallback(() => {
    return countries.find(c => c.isDefault) || (countries.length > 0 ? countries[0] : null);
  }, [countries]);

  const refreshCountries = useCallback(() => {
    return fetchCountries(true);
  }, [fetchCountries]);

  // Initial fetch on mount
  useEffect(() => {
    if (countries.length === 0) {
      fetchCountries();
    }
  }, []);

  const value = {
    countries,
    loading,
    error,
    fetchCountries,
    refreshCountries,
    getCountryById,
    getCountryByCode,
    getDefaultCountry
  };

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
};
